<template>
  <div class="mobile-page">
    <div class="sub-nav">
      <button class="back-btn" @click="back"><el-icon :size="18"><ArrowLeft /></el-icon></button>
      <span class="nav-title">隐患详情</span>
      <span class="nav-right"><button class="refresh-btn" @click="load"><el-icon :size="16"><Refresh /></el-icon></button></span>
    </div>

    <div class="mobile-body">
      <div v-if="loading" class="center-box">
        <el-icon class="is-loading" :size="28"><Loading /></el-icon>
        <p class="sub-text">加载中…</p>
      </div>
      <div v-else-if="!h" class="white-card center-box">
        <el-icon :size="40" color="#c0c4cc"><Warning /></el-icon>
        <p class="sub-text">{{ error || '未找到该记录' }}</p>
      </div>

      <template v-else>
        <!-- 状态头 -->
        <div class="white-card head-card">
          <div class="hc-top">
            <span class="hc-id mono">{{ h.id }}</span>
            <span v-if="h.level" class="pill" :class="levelPill(h.level)">{{ h.level }}</span>
          </div>
          <div class="hc-status-line">
            <span class="status-badge" :class="{ red: !h.closed && h.status === '待处置', orange: !h.closed && h.status === '处置中', green: h.closed }">
              {{ h.closed ? '已关闭' : h.status }}
            </span>
            <span class="hc-times">{{ fmt(h.created_at) }} 上报</span>
          </div>
          <div class="hc-desc">{{ h.description }}</div>
        </div>

        <!-- 基本信息 -->
        <div class="white-card">
          <div class="info-row"><span class="info-k">位置</span><span class="info-v">{{ h.location || '—' }}</span></div>
          <div class="info-row"><span class="info-k">来源</span><span class="info-v">{{ h.source || '—' }}<template v-if="h.item_name">（{{ h.item_name }}）</template></span></div>
          <div class="info-row"><span class="info-k">上报人</span><span class="info-v">{{ h.reporter || '—' }}<template v-if="h.department"> · {{ h.department }}</template></span></div>
          <div class="info-row" v-if="h.role_type"><span class="info-k">身份</span><span class="info-v">{{ h.role_type }}<template v-if="h.identity_reason">（{{ h.identity_reason }}）</template></span></div>
          <div class="info-row" v-if="h.source_record_id"><span class="info-k">巡检记录</span><span class="info-v mono">{{ h.source_record_id }}</span></div>
        </div>

        <!-- 闭环流程 -->
        <div class="white-card">
          <div class="wc-title">闭环流程</div>
          <div class="flow-wrap">
            <template v-for="(s, i) in flowSteps" :key="s.label">
              <div class="flow-node">
                <span class="fdot" :class="{ done: s.done, cur: s.cur }">
                  <el-icon v-if="s.done || s.cur" :size="12"><Check /></el-icon>
                </span>
                <span class="flabel" :class="{ cur: s.cur }">{{ s.label }}</span>
              </div>
              <span v-if="i < flowSteps.length - 1" class="fline" :class="{ done: flowSteps[i + 1].done || flowSteps[i + 1].cur }"></span>
            </template>
          </div>
        </div>

        <!-- 现场照片/视频：默认不拉取，点击加载 -->
        <div class="white-card" v-if="photoTotal > 0 || videoTotal > 0">
          <div class="wc-title row-between">
            <span>现场材料</span>
            <span v-if="!mediaLoaded" class="media-hint">{{ mediaLoading ? '加载中…' : '已跳过，点击加载' }}</span>
          </div>

          <template v-if="mediaLoading">
            <div class="center-box" style="padding:18px">
              <el-icon class="is-loading" :size="22"><Loading /></el-icon>
              <p class="sub-text">正在加载照片…</p>
            </div>
          </template>

          <template v-else-if="!mediaLoaded">
            <div class="load-media-btn" @click="loadMedia">
              <el-icon :size="20"><CameraFilled /></el-icon>
              <span>加载现场照片{{ photoTotal ? '（' + photoTotal + ' 张）' : '' }}{{ videoTotal ? ' / 视频（' + videoTotal + ' 段）' : '' }}</span>
            </div>
            <p class="tiny-tip">为节省流量，照片与视频默认不下载，按需加载。</p>
          </template>

          <template v-else>
            <div v-if="photos.length" class="photo-grid">
              <div class="p-item" v-for="(p, i) in photos" :key="'p' + i" @click="openViewer('photos', i)">
                <img :src="p" loading="lazy" />
              </div>
            </div>
            <div v-if="videos.length" class="video-list">
              <div class="v-item" v-for="(v, i) in videos" :key="'v' + i">
                <video :src="v" controls playsinline preload="none"></video>
                <div class="v-caption">现场视频 {{ i + 1 }}</div>
              </div>
            </div>
            <div v-if="!photos.length && !videos.length" class="center-box" style="padding:16px">
              <p class="sub-text">该隐患没有照片或视频</p>
            </div>
          </template>
        </div>

        <!-- 处置记录 -->
        <div class="white-card" v-if="logs.length">
          <div class="wc-title row-between">
            <span>处置记录（{{ logs.length }}）</span>
            <span v-if="!mediaLoaded && logPhotoTotal > 0" class="media-hint">凭证未加载</span>
          </div>
          <div class="log-list">
            <div class="log-item" v-for="(lg, i) in logs" :key="i">
              <div class="log-head">
                <span class="log-action">{{ actionText(lg.action) }}</span>
                <span class="log-time">{{ fmt(lg.time) }}</span>
              </div>
              <div class="log-remark" v-if="lg.remark">{{ lg.remark }}</div>
              <div class="log-meta">
                <span v-if="lg.handler">处置人：{{ lg.handler }}</span>
                <span v-if="!mediaLoaded && lg.photoCount"> · 凭证 {{ lg.photoCount }} 张</span>
              </div>
              <div class="log-photos" v-if="lg.photos && lg.photos.length">
                <img v-for="(p, pi) in lg.photos" :key="pi" :src="p" @click="openLogPhoto(i, pi)" />
              </div>
            </div>
          </div>
        </div>
        <div v-else class="white-card center-box" style="padding:18px">
          <p class="sub-text" style="color:#9aa0a6">暂无处置记录，等待相关人员进行闭环处理</p>
        </div>
      </template>
    </div>

    <!-- 全屏查看器 -->
    <teleport to="body">
      <transition name="viewer">
        <div v-if="viewerVisible" class="viewer" @click.self="viewerVisible = false">
          <div class="viewer-top">
            <button @click="viewerVisible = false"><el-icon :size="22"><Close /></el-icon></button>
            <span class="viewer-count">{{ viewerIndex + 1 }} / {{ viewerList.length }}</span>
            <button @click="downloadCurrent"><el-icon :size="20"><Download /></el-icon></button>
          </div>
          <div class="viewer-body">
            <img :src="viewerList[viewerIndex]" />
          </div>
          <div class="viewer-btns">
            <button :disabled="viewerIndex === 0" @click="viewerIndex--"><el-icon><ArrowLeft /></el-icon></button>
            <button :disabled="viewerIndex >= viewerList.length - 1" @click="viewerIndex++"><el-icon><ArrowRight /></el-icon></button>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fetchHazards, fetchHazardMedia } from '../api'
import dayjs from 'dayjs'

const route = useRoute()
const router = useRouter()
function back() { router.back() }

const h = ref(null)
const loading = ref(true)
const error = ref('')
const mediaLoaded = ref(false)
const mediaLoading = ref(false)
const media = ref(null)

const photoTotal = computed(() => h.value?.photoCount || 0)
const videoTotal = computed(() => h.value?.videoCount || 0)
const photos = computed(() => (media.value?.photos || []).filter(p => typeof p === 'string' && p))
const videos = computed(() => (media.value?.videos || []).filter(v => typeof v === 'string' && v))

/* 处置记录：未加载媒体时显示轻量日志（无凭证），加载后带凭证 */
const logs = computed(() => {
  const base = h.value?.handle_log || []
  if (!media.value) return base
  const full = media.value.handle_log || []
  return base.map((lg, i) => {
    const m = full[i] || {}
    return { ...lg, photos: m.photos || [], videos: m.videos || [] }
  })
})
const logPhotoTotal = computed(() => (h.value?.handle_log || []).reduce((s, lg) => s + (lg.photoCount || 0), 0))

const flowSteps = computed(() => {
  if (!h.value) return []
  const cur = h.value.closed ? '已关闭' : (h.value.status || '待处置')
  const order = ['待处置', '处置中', '已完成', '已关闭']
  const ci = order.indexOf(cur)
  return order.map((label, idx) => {
    if (cur === '已关闭') return { label, done: idx < order.length - 1, cur: idx === order.length - 1 }
    return { label, done: idx < ci, cur: idx === ci }
  })
})

/* 查看器 */
const viewerVisible = ref(false)
const viewerList = ref([])
const viewerIndex = ref(0)

function openViewer(kind, i) {
  const list = kind === 'photos' ? photos.value : []
  viewerList.value = list
  viewerIndex.value = i
  if (list.length) viewerVisible.value = true
}
function openLogPhoto(logIdx, photoIdx) {
  const lg = logs.value[logIdx]
  if (!lg || !lg.photos || !lg.photos.length) return
  viewerList.value = lg.photos
  viewerIndex.value = photoIdx
  viewerVisible.value = true
}

function downloadCurrent() {
  const url = viewerList.value[viewerIndex.value]
  if (!url) return
  const a = document.createElement('a')
  a.href = url
  a.download = 'photo_' + Date.now() + '.jpg'
  a.click()
}

async function loadMedia() {
  if (mediaLoaded.value || mediaLoading.value) return
  mediaLoading.value = true
  try {
    const r = await fetchHazardMedia(route.params.id)
    media.value = r.data || null
    mediaLoaded.value = true
  } catch (e) {
    // 媒体加载失败不影响文本查看
    mediaLoaded.value = true
    media.value = null
    // eslint-disable-next-line no-console
    console.error('媒体加载失败', e)
  } finally {
    mediaLoading.value = false
  }
}

function actionText(a) {
  const map = { 处置中: '开始处置', 处置完成: '处置完成', 补充说明: '补充说明', 更新: '状态更新' }
  return map[a] || a
}

function fmt(t) {
  return t ? dayjs(t).format('YYYY-MM-DD HH:mm') : ''
}
function levelPill(l) {
  if (l === '紧急' || l === '重大') return 'pill-red'
  if (l === '较大') return 'pill-orange'
  if (l === '一般') return 'pill-blue'
  return 'pill-gray'
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const fromState = route.state && route.state.hazard
    if (fromState && fromState.id === route.params.id && fromState.photoCount !== undefined) {
      h.value = fromState
    } else {
      const r = await fetchHazards({ all: 1, light: 1 })
      const list = r.data || []
      h.value = list.find(x => x.id === route.params.id) || null
    }
    if (!h.value) error.value = '未找到该隐患（' + route.params.id + '）'
  } catch (e) {
    error.value = e.message || '加载失败'
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.head-card { margin-bottom: 10px; }
.hc-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.hc-id { font-size: 13px; font-weight: 700; color: #5f6368; }
.hc-status-line { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.status-badge { font-size: 13px; font-weight: 700; padding: 4px 14px; border-radius: 18px; }
.status-badge.red { background: #fce8e6; color: #c5221f; }
.status-badge.orange { background: #fef7e0; color: #b06000; }
.status-badge.green { background: #e6f4ea; color: #188038; }
.hc-times { font-size: 12px; color: #9aa0a6; }
.hc-desc { font-size: 16px; font-weight: 600; color: #202124; line-height: 1.6; }

.row-between { display: flex; justify-content: space-between; align-items: center; }
.media-hint { font-size: 11px; font-weight: 400; color: #9aa0a6; }

.wc-title { font-size: 13.5px; font-weight: 700; color: #303133; margin-bottom: 10px; }
.info-row { display: flex; gap: 10px; padding: 7px 0; font-size: 13.5px; border-bottom: 1px solid #f6f7f9; }
.info-row:last-child { border-bottom: none; }
.info-k { width: 62px; flex-shrink: 0; color: #9aa0a6; }
.info-v { color: #3c4043; word-break: break-all; line-height: 1.5; }

/* 加载媒体按钮 */
.load-media-btn {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  background: #e8f0fe; color: #1a73e8; border: 1.5px dashed #8ab4f8;
  border-radius: 12px; padding: 14px; font-size: 14px; font-weight: 600; cursor: pointer;
}
.load-media-btn:active { background: #d2e3fc; }
.tiny-tip { font-size: 11px; color: #b0b3bb; text-align: center; margin-top: 6px; }

/* 流程 */
.flow-wrap { display: flex; align-items: center; padding: 6px 0 2px; }
.flow-node { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.fdot {
  width: 24px; height: 24px; border-radius: 50%; background: #e8eaed; color: #fff;
  display: flex; align-items: center; justify-content: center;
}
.fdot.done { background: #34a853; }
.fdot.cur { background: #1a73e8; box-shadow: 0 0 0 3px rgba(26,115,232,.18); }
.flabel { font-size: 11px; color: #9aa0a6; }
.flabel.cur { color: #1a73e8; font-weight: 700; }
.fline { flex: 1; height: 3px; background: #e8eaed; margin: 0 2px 14px; border-radius: 2px; }
.fline.done { background: #34a853; }

/* 照片 */
.photo-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.p-item { aspect-ratio: 1; border-radius: 10px; overflow: hidden; background: #f1f3f4; cursor: pointer; }
.p-item img { width: 100%; height: 100%; object-fit: cover; display: block; }
.video-list { display: flex; flex-direction: column; gap: 10px; }
.v-item video { width: 100%; border-radius: 10px; background: #000; max-height: 60vh; }
.v-caption { font-size: 12px; color: #9aa0a6; margin-top: 4px; }

/* 日志 */
.log-list { display: flex; flex-direction: column; gap: 10px; }
.log-item { border: 1px solid #eef0f3; border-radius: 12px; padding: 10px 12px; background: #fafbfc; }
.log-head { display: flex; justify-content: space-between; align-items: center; }
.log-action { font-size: 13px; font-weight: 700; color: #1a73e8; }
.log-time { font-size: 11.5px; color: #9aa0a6; }
.log-remark { font-size: 13.5px; color: #3c4043; margin-top: 6px; line-height: 1.55; }
.log-meta { font-size: 12px; color: #9aa0a6; margin-top: 4px; }
.log-photos { display: flex; gap: 6px; margin-top: 8px; flex-wrap: wrap; }
.log-photos img { width: 64px; height: 64px; border-radius: 8px; object-fit: cover; cursor: pointer; border: 1px solid #e8eaed; }
</style>
